import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import MoneyComponent from '../elements/MoneyComponent';
import CurrencyInput from '../elements/CurrencyInput';
import TextInput from '../elements/TextInput.jsx';
import MaterialIcon from '../elements/MaterialIcon.jsx';
import InfoTip from '../elements/InfoTip.jsx';
import { saveLoanTerms } from '../../redux/actions/dashboard.js';

/** An amount starts at 0.00, so focusing one selects it and typing replaces it rather than adding to it. */
function selectAll(event) {
	event.target.select();
}

/** A rate as typed: '5.74' for the stored '5.740', '' for none. */
export function rateText(rate) {
	const value = parseFloat(rate);
	return Number.isFinite(value) ? String(value) : '';
}

/** The month's own terms from its budget row, as a form starts them. */
export function monthTerms(category, date) {
	const budget = category.getBudget(date, false);
	return {
		balance: Number(budget.fund_total) || 0,
		rate: rateText(budget.interest_amount),
		minimum: Number(budget.minimum_payment) || 0,
		note: budget.notes || '',
	};
}

/** The kinds of down payment, as the server names them, and what a person calls each. */
export const downPaymentKinds = {
	cash: 'Cash',
	credit: 'Credit or rebate',
	trade_in: 'Trade-in',
};

const shortKindNames = {
	cash: 'cash',
	credit: 'credit',
	trade_in: 'trade-in',
};

/**
 * A loan's down payment rows summed by kind, cash first, as the card shows them: "$1,900.00 + $4,000.00
 * credit". Kinds with nothing are left out.
 *
 * @return {{kind: string, amount: number, label: string}[]}
 */
export function downPaymentParts(rows = []) {
	return Object.keys(downPaymentKinds).map((kind) => ({
		kind,
		amount: rows.filter((row) => row.kind === kind).reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
		label: shortKindNames[kind],
	})).filter((part) => part.amount > 0);
}

/** "$1,900.00 + $4,000.00 credit": the first part large, the rest small beside it. */
export function DownPaymentSummary({ rows }) {
	const parts = downPaymentParts(rows);
	if(!parts.length) {
		return <span className='loan_payoff__muted'>Not recorded</span>;
	}
	const [first, ...rest] = parts;
	return (
		<React.Fragment>
			<MoneyComponent amount={first.amount} />
			{first.kind !== 'cash' && <small> {first.label}</small>}
			{rest.map((part) => (
				<small key={part.kind}> + <MoneyComponent amount={part.amount} /> {part.label}</small>
			))}
		</React.Fragment>
	);
}

/**
 * The loan's terms as they began: the month its balance starts from, or its latest refinance. The
 * amount financed, rate, minimum and note are that month's budget row, the same one the budget page
 * edits. The down payment is the loan's own list of rows: the cash put down, and any credit, rebate
 * or trade-in, so money that was never yours is not counted as yours. Edit saves each where it lives.
 */
/** What each of a loan's terms means, for the help icon beside its label. */
export const termsTips = {
	amountFinanced: 'The balance in the month these terms began: when the loan opened, or when its rate last changed, as in a refinance.',
	downPayment: 'Paid up front when the loan opened, cash and credits.',
	rate: 'The yearly interest rate on these terms. A month\'s interest is a twelfth of it.',
	minimum: 'The required monthly payment recorded for these terms. The projection pays it each month, unless the loan has lately been paid more.',
};

export function LoanTermsDetails({ loan }) {
	const dispatch = useDispatch();
	const { category } = loan;
	const start = loan.termsStart();
	const terms = monthTerms(category, start);
	const downPayments = category.down_payments || [];
	const refinanced = !!loan.termsStartDate;

	const [editing, updateEditing] = useState(false);
	const [saving, updateSaving] = useState(false);
	const [form, updateForm] = useState(null);

	function startEditing() {
		updateForm({
			...terms,
			downPayments: downPayments.map(({ kind, amount, note }) => ({ kind, amount, note: note || '' })),
		});
		updateEditing(true);
	}

	function change(field) {
		return (value) => updateForm({ ...form, [field]: value?.target ? value.target.value : value });
	}

	function changeDownPayment(index, field) {
		return (value) => {
			const downPayments = form.downPayments.slice();
			downPayments[index] = { ...downPayments[index], [field]: value?.target ? value.target.value : value };
			updateForm({ ...form, downPayments });
		};
	}

	function addDownPayment() {
		const kind = form.downPayments.some((row) => row.kind === 'cash') ? 'credit' : 'cash';
		updateForm({ ...form, downPayments: [...form.downPayments, { kind, amount: 0, note: '' }] });
	}

	function removeDownPayment(index) {
		updateForm({ ...form, downPayments: form.downPayments.filter((row, at) => at !== index) });
	}

	function save(event) {
		event.preventDefault();
		updateSaving(true);
		dispatch(saveLoanTerms(category, start, form))
			.then(() => updateEditing(false), () => {})
			.then(() => updateSaving(false));
	}

	const heading = `${refinanced ? 'Refinanced' : 'Opened'} ${start.format('MMM YYYY')}`;

	if(!editing) {
		const noted = downPayments.length > 1 || downPayments.some((row) => row.note);
		return (
			<div className='loan_payoff__terms'>
				<div className='loan_payoff__terms-header'>
					<span>{heading}</span>
					<button type='button' className='btn-flat loan_payoff__terms-edit' onClick={startEditing}>
						<MaterialIcon>edit</MaterialIcon> Edit terms
					</button>
				</div>
				<dl className='loan_payoff__facts loan_payoff__terms-facts'>
					<div>
						<dt>Amount financed<InfoTip>{termsTips.amountFinanced}</InfoTip></dt>
						<dd><MoneyComponent amount={terms.balance} /></dd>
					</div>
					<div>
						<dt>Down payment<InfoTip>{termsTips.downPayment}</InfoTip></dt>
						<dd><DownPaymentSummary rows={downPayments} /></dd>
					</div>
					<div>
						<dt>Rate<InfoTip>{termsTips.rate}</InfoTip></dt>
						<dd>{terms.rate === '' ? <span className='loan_payoff__muted'>None</span> : `${terms.rate}%`}</dd>
					</div>
					<div>
						<dt>Minimum<InfoTip>{termsTips.minimum}</InfoTip></dt>
						<dd>{terms.minimum ? <MoneyComponent amount={terms.minimum} /> : <span className='loan_payoff__muted'>None</span>}</dd>
					</div>
				</dl>
				{noted &&
				<ul className='loan_payoff__down-payments'>
					{downPayments.map((row) => (
						<li key={row.id}>
							<span className='loan_payoff__down-payment-kind'>{downPaymentKinds[row.kind]}</span>
							<MoneyComponent amount={row.amount} />
							{row.note && <span className='loan_payoff__muted'>{row.note}</span>}
						</li>
					))}
				</ul>
				}
				{!!terms.note && <p className='loan_payoff__terms-note'>{terms.note}</p>}
			</div>
		);
	}

	return (
		<form className='loan_payoff__terms is-editing' onSubmit={save}>
			<div className='loan_payoff__terms-header'>
				<span>{heading}</span>
			</div>
			<div className='loan_payoff__terms-fields'>
				<div className='input-field'>
					<CurrencyInput onFocus={selectAll} label='Amount financed' name='balance' allowNegative={false} value={form.balance} onChange={change('balance')} />
				</div>
				<div className='input-field'>
					<TextInput label='Rate %' name='rate' value={form.rate} onChange={change('rate')} autoComplete='off' />
				</div>
				<div className='input-field'>
					<CurrencyInput onFocus={selectAll} label='Minimum' name='minimum' allowNegative={false} value={form.minimum} onChange={change('minimum')} />
				</div>
				<div className='input-field loan_payoff__terms-note-field'>
					<TextInput label='Note' name='note' textarea={true} value={form.note} onChange={change('note')} />
				</div>
			</div>
			<fieldset className='loan_payoff__down-payment-fields'>
				<legend>Down payment</legend>
				{form.downPayments.map((row, index) => (
					<div className='loan_payoff__down-payment-row' key={index}>
						<select
							className='browser-default'
							name={`downPayments.${index}.kind`}
							aria-label='Kind'
							value={row.kind}
							onChange={changeDownPayment(index, 'kind')}
						>
							{Object.entries(downPaymentKinds).map(([kind, name]) => (
								<option key={kind} value={kind}>{name}</option>
							))}
						</select>
						<div className='input-field'>
							<CurrencyInput onFocus={selectAll} label='Amount' name={`downPayments.${index}.amount`} allowNegative={false} value={row.amount} onChange={changeDownPayment(index, 'amount')} />
						</div>
						<div className='input-field'>
							<TextInput label='Note' name={`downPayments.${index}.note`} value={row.note} onChange={changeDownPayment(index, 'note')} autoComplete='off' />
						</div>
						<button type='button' className='btn-flat loan_payoff__down-payment-remove' title='Remove' onClick={() => removeDownPayment(index)}>
							<MaterialIcon>close</MaterialIcon>
						</button>
					</div>
				))}
				<button type='button' className='btn-flat loan_payoff__terms-edit' onClick={addDownPayment}>
					<MaterialIcon>add</MaterialIcon> Add cash, a credit or a trade-in
				</button>
			</fieldset>
			<p className='loan_payoff__hint'>
				The amount financed, rate and minimum are {start.format('MMMM YYYY')}'s on the budget. A later
				statement goes on its month, in the schedule below. A credit, rebate or trade-in counts toward
				the price without being your cash.
			</p>
			<div className='loan_payoff__terms-actions'>
				<button type='button' className='btn-flat' onClick={() => updateEditing(false)} disabled={saving}>Cancel</button>
				<button type='submit' className='btn' disabled={saving || !(Number(form.balance) > 0)}>Save</button>
			</div>
		</form>
	);
}

/**
 * One month of the schedule, opened for editing: a statement's balance, and a rate or minimum when
 * they changed that month. Empty leaves the month to the projection, or to the month before.
 */
export function LoanMonthEditor({ category, date, projectedBalance, isStart, onClose }) {
	const dispatch = useDispatch();
	const [form, updateForm] = useState(() => {
		const { balance, rate, minimum } = monthTerms(category, date);
		return { balance, rate, minimum };
	});
	const [saving, updateSaving] = useState(false);

	function change(field) {
		return (value) => updateForm({ ...form, [field]: value?.target ? value.target.value : value });
	}

	function save(event) {
		event.preventDefault();
		updateSaving(true);
		dispatch(saveLoanTerms(category, date, form))
			.then(onClose, () => updateSaving(false));
	}

	return (
		<form className='loan_payoff__month-form' onSubmit={save}>
			<div className='loan_payoff__terms-fields'>
				<div className='input-field'>
					<CurrencyInput onFocus={selectAll} label={`Balance, ${date.format('MMM YYYY')}`} name='balance' allowNegative={false} value={form.balance} onChange={change('balance')} />
				</div>
				<div className='input-field'>
					<TextInput label='Rate %' name='rate' value={form.rate} onChange={change('rate')} autoComplete='off' />
				</div>
				<div className='input-field'>
					<CurrencyInput onFocus={selectAll} label='Minimum' name='minimum' allowNegative={false} value={form.minimum} onChange={change('minimum')} />
				</div>
			</div>
			<p className='loan_payoff__hint'>
				{isStart
					? 'The loan starts from this balance, so it cannot be left empty. '
					: <React.Fragment>Leave the balance at 0 to use the projected <MoneyComponent amount={projectedBalance || 0} />. </React.Fragment>}
				Leave the rate empty, or the minimum at 0, to carry on the month before's.
			</p>
			<div className='loan_payoff__terms-actions'>
				<button type='button' className='btn-flat' onClick={onClose} disabled={saving}>Cancel</button>
				<button type='submit' className='btn' disabled={saving || (isStart && !(Number(form.balance) > 0))}>Save</button>
			</div>
		</form>
	);
}
